import { ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  platformMeta, relativeDate, exactDate,
  CONFIDENCE_STYLES, VALUE_STYLES,
} from "@/lib/format";

export const IntelCard = ({ ev }) => {
  const primary = platformMeta(ev.primary_platform);
  const PIcon = primary.icon;
  const dateStr = ev.event_date || ev.sources?.[0]?.published_at || "";

  return (
    <article
      data-testid="intel-card"
      className="group rounded-xl border border-[#262933] bg-[#12141A] p-5 hover:border-[#E8833A]/40 transition-colors duration-200"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="flex items-center justify-center h-7 w-7 rounded-md bg-[#1A1D26] border border-[#262933] shrink-0"
            style={{ color: primary.color }}
          >
            <PIcon className="h-4 w-4" />
          </span>
          <span className="font-mono text-[11px] uppercase tracking-widest text-[#9A9CA6] truncate">
            {primary.label} · {ev.who_posted}
          </span>
        </div>
        <span
          className={cn("shrink-0 px-2 py-0.5 rounded-md border text-[10px] font-mono uppercase tracking-wider font-semibold", CONFIDENCE_STYLES[ev.confidence])}
          data-testid="intel-confidence-badge"
        >
          {ev.confidence}
        </span>
      </div>

      <h4 className="font-display font-semibold text-[#F3EFE6] text-[15px] leading-snug mb-1.5">
        {ev.headline}
      </h4>
      <p className="text-sm text-[#B8BAC4] leading-relaxed mb-3">{ev.factual_summary}</p>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span
          className={cn("px-2 py-0.5 rounded-md border text-[10px] font-mono uppercase tracking-wider font-semibold", VALUE_STYLES[ev.interview_value])}
          data-testid="intel-value-badge"
        >
          {ev.interview_value} value
        </span>
        <span className="px-2 py-0.5 rounded-md border border-[#262933] bg-[#1A1D26] text-[10px] font-mono uppercase tracking-wider text-[#9A9CA6]">
          {ev.category}
        </span>
      </div>

      <div className="flex items-center justify-between gap-3 pt-3 border-t border-[#1E212A]">
        <span
          className="font-mono text-[11px] text-[#686B77]"
          title={exactDate(dateStr)}
          data-testid="intel-date"
        >
          {relativeDate(dateStr)}
          {dateStr && <span className="hidden md:inline"> · {exactDate(dateStr)}</span>}
        </span>
        <div className="flex items-center gap-1.5">
          {(ev.sources || []).slice(0, 4).map((s, i) => {
            const m = platformMeta(s.platform);
            const SIcon = m.icon;
            return (
              <a
                key={i}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="intel-source-link"
                title={`${m.label}: ${s.url}`}
                className="flex items-center gap-1 px-2 py-1 rounded-md bg-[#1A1D26] border border-[#262933] hover:border-[#E8833A]/50 hover:-translate-y-0.5 transition-transform duration-150"
                style={{ color: m.color }}
              >
                <SIcon className="h-3.5 w-3.5" />
                <ExternalLink className="h-3 w-3 text-[#686B77]" />
              </a>
            );
          })}
        </div>
      </div>
    </article>
  );
};
